"use client";

import * as React from "react";
import { useInView, useMotionValue, useTransform, animate } from "motion/react";
import { cn } from "@/lib/utils";

interface AnimatedStatProps {
  value: number;
  duration?: number;
  decimals?: number;
  suffix?: string;
  prefix?: string;
  className?: string;
}

export function AnimatedStat({
  value,
  duration = 1.8,
  decimals = 0,
  suffix = "",
  prefix = "",
  className,
}: AnimatedStatProps) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => {
    const factor = Math.pow(10, decimals);
    return Math.round(latest * factor) / factor;
  });
  const [display, setDisplay] = React.useState("0");

  React.useEffect(() => {
    if (!inView) return;
    const controls = animate(motionValue, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
    });
    const unsubscribe = rounded.on("change", (v) => {
      if (decimals > 0) setDisplay(v.toFixed(decimals));
      else setDisplay(Math.floor(v).toString());
    });
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [inView, value, duration, decimals, motionValue, rounded]);

  return (
    <span ref={ref} className={cn("tabular-nums", className)}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
